'use client';

import { useAppSelector } from '@/state';
import { ProjectManagementDashboardApi } from '@/state/api';
import {
  Badge,
  Box,
  Button,
  Center,
  Flex,
  NativeSelect,
  Separator,
  Spinner,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Card, toasterApi } from '@ui';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useMemo, useState } from 'react';

import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

dayjs.extend(utc);

// -----------------------------
// utils
// -----------------------------
const fmtDateTime = (v: any) => {
  if (!v) return '—';
  const d = dayjs(v);
  return d.isValid() ? d.format('MM/DD/YY') : '—';
};

const moneyToNumber = (v: any) => {
  const s = String(v ?? '').trim();
  if (!s) return 0;
  const n = Number(s.replace(/[$, ]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

const fmtMoney = (v: any) => {
  const n = moneyToNumber(v);
  return `$${n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const toneBadge = (tone?: 'red' | 'yellow' | 'green' | 'gray' | 'purple') => {
  if (tone === 'red') return 'red';
  if (tone === 'yellow') return 'yellow';
  if (tone === 'green') return 'green';
  if (tone === 'purple') return 'purple';
  return 'gray';
};

const formatStatusLabel = (status?: string) => {
  const s = (status || '').toLowerCase();

  if (s === 'resubmittal_required') return 'Resubmittal Required';
  if (s === 'submitted') return 'Submitted';
  if (s === 'closed') return 'Closed';
  if (s === 'void') return 'Void';
  if (s === 'draft') return 'Draft';
  if (s === 'approved') return 'Approved';
  if (s === 'rejected') return 'Rejected';
  if (s === 'rom') return 'ROM';
  if (s === 'converted') return 'Converted';

  return status || '—';
};

const statusBadge = (status?: string) => {
  const s = (status || '').toLowerCase();

  if (['closed', 'approved'].includes(s)) {
    return { colorPalette: 'green', label: formatStatusLabel(status) };
  }

  if (['resubmittal_required', 'rejected'].includes(s)) {
    return { colorPalette: 'red', label: formatStatusLabel(status) };
  }

  if (['void', 'converted'].includes(s)) {
    return { colorPalette: 'purple', label: formatStatusLabel(status) };
  }

  if (['submitted', 'draft', 'rom'].includes(s)) {
    return { colorPalette: 'gray', label: formatStatusLabel(status) };
  }

  return { colorPalette: 'gray', label: formatStatusLabel(status) };
};

const typeBadge = (t: string) => {
  const x = (t || '').toLowerCase();
  if (x === 'rfi') return { colorPalette: 'blue', label: 'RFI' };
  if (x === 'submittal') return { colorPalette: 'teal', label: 'Submittal' };
  if (x === 'pending_change')
    return { colorPalette: 'purple', label: 'Change Order' };
  return { colorPalette: 'gray', label: t || 'Item' };
};

const rowBgForAction = (r: any) => {
  const status = String(r?.status || '').toLowerCase();

  if (status === 'closed') return { base: 'green.50', _dark: 'green.950' };
  if (status === 'resubmittal_required')
    return { base: 'red.50', _dark: 'red.950' };
  if (status === 'void') return { base: 'purple.50', _dark: 'purple.950' };

  if (r?.is_overdue) return { base: 'red.50', _dark: 'red.950' };
  if (r?.due_at) return { base: 'gray.50', _dark: 'gray.900' };
  return undefined;
};

const isClosedLikeStatus = (status?: string) => {
  const s = String(status || '').toLowerCase();
  return s === 'closed';
};

// -----------------------------
// Recharts helpers
// -----------------------------
const statusToColor = (status: string) => {
  const s = (status || '').toLowerCase();

  if (s === 'closed' || s === 'approved') return '#22c55e';
  if (s === 'submitted') return '#cbd5e1';
  if (s === 'draft') return '#94a3b8';
  if (s === 'resubmittal_required' || s === 'rejected') return '#ef4444';
  if (s === 'void' || s === 'converted') return '#a855f7';
  if (s === 'rom') return '#f59e0b';

  return '#cbd5e1';
};

const DonutTooltip = ({ active, payload, label, metric }: any) => {
  if (!active || !payload?.length) return null;
  const p = payload[0]?.payload;
  const name = p?.name || label || '—';
  const count = Number(p?.count || 0);
  const amount = moneyToNumber(p?.amount);

  return (
    <Box
      bg={{ base: 'gray.900', _dark: 'gray.900' }}
      borderWidth="1px"
      rounded="md"
      px={3}
      py={2}
      boxShadow="lg"
    >
      <Text fontWeight="bold" fontSize="sm">
        {name}
      </Text>
      <Text fontSize="xs" opacity={0.85}>
        Count: <b>{count}</b>
      </Text>
      <Text fontSize="xs" opacity={0.85}>
        Amount: <b>{fmtMoney(amount)}</b>
      </Text>
      <Text fontSize="xs" opacity={0.7}>
        Showing: <b>{metric === 'amount' ? 'Amount' : 'Count'}</b>
      </Text>
    </Box>
  );
};

const ChangeOrderDonut = ({
  rows,
  metric,
}: {
  rows: any[];
  metric: 'amount' | 'count';
}) => {
  const data = useMemo(() => {
    return (rows ?? [])
      .map((r: any) => ({
        name: formatStatusLabel(String(r.status || '')),
        status: String(r.status || ''),
        count: Number(r.count || 0),
        amount: moneyToNumber(r.amount),
        value:
          metric === 'amount' ? moneyToNumber(r.amount) : Number(r.count || 0),
      }))
      .filter((x: any) => x.value > 0);
  }, [rows, metric]);

  const total = useMemo(
    () => data.reduce((acc: number, r: any) => acc + (r.value || 0), 0),
    [data],
  );

  if (!data.length || total <= 0) {
    return (
      <Center py={4}>
        <Text opacity={0.7}>No data.</Text>
      </Center>
    );
  }

  return (
    <Box height="180px" width="100%">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={data}
            dataKey="value"
            nameKey="name"
            innerRadius={55}
            outerRadius={82}
            paddingAngle={2}
            stroke="rgba(255,255,255,0.12)"
          >
            {data.map((entry: any) => (
              <Cell key={entry.status} fill={statusToColor(entry.status)} />
            ))}
          </Pie>
          <Tooltip
            content={(p: any) => <DonutTooltip {...p} metric={metric} />}
          />
        </PieChart>
      </ResponsiveContainer>
    </Box>
  );
};

const SimpleBarTooltip = ({ active, payload, label, valueLabel }: any) => {
  if (!active || !payload?.length) return null;
  const p = payload[0]?.payload;
  return (
    <Box
      bg={{ base: 'gray.900', _dark: 'gray.900' }}
      borderWidth="1px"
      rounded="md"
      px={3}
      py={2}
      boxShadow="lg"
    >
      <Text fontWeight="bold" fontSize="sm">
        {label}
      </Text>
      <Text fontSize="xs" opacity={0.85}>
        {valueLabel}: <b>{p?.value ?? 0}</b>
      </Text>
    </Box>
  );
};

const barColorFor = (name: string) => {
  const n = String(name || '').toLowerCase();

  if (n.includes('overdue')) return '#ef4444';
  if (n.includes('blocking')) return '#ef4444';
  if (n.includes('resubmittal')) return '#ef4444';
  if (n.includes('closed')) return '#22c55e';
  if (n.includes('void')) return '#a855f7';
  if (n.includes('submitted')) return '#cbd5e1';
  if (n.includes('draft')) return '#94a3b8';

  return '#cbd5e1';
};

const CompactBarChart = ({
  data,
  height = 250,
  valueLabel = 'Count',
}: {
  data: { name: string; value: number }[];
  height?: number;
  valueLabel?: string;
}) => {
  if (!data?.length) {
    return (
      <Center py={4}>
        <Text opacity={0.7}>No data.</Text>
      </Center>
    );
  }

  const longestLabel = Math.max(...data.map((d) => String(d.name || '').length));
  const yAxisWidth = Math.min(180, Math.max(96, longestLabel * 6));

  return (
    <Box height={`${height}px`} width="100%">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 8, right: 12, bottom: 8, left: 2 }}
          barCategoryGap={10}
        >
          <CartesianGrid
            stroke="rgba(148, 163, 184, 0.28)"
            strokeWidth={1}
            horizontal={false}
          />
          <XAxis
            type="number"
            tick={{ fontSize: 12, fill: 'rgba(148, 163, 184, 0.92)' }}
            axisLine={false}
            tickLine={false}
            allowDecimals={false}
          />
          <YAxis
            type="category"
            dataKey="name"
            width={yAxisWidth}
            tick={{ fontSize: 12, fill: 'rgba(226, 232, 240, 0.98)' }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            cursor={{ fill: 'rgba(148, 163, 184, 0.10)' }}
            content={(p: any) => (
              <SimpleBarTooltip {...p} valueLabel={valueLabel} />
            )}
          />
          <Bar dataKey="value" radius={[0, 8, 8, 0]} maxBarSize={42}>
            {data.map((entry, idx) => (
              <Cell key={`cell-${idx}`} fill={barColorFor(entry.name)} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

// -----------------------------
// page
// -----------------------------
const Plan = () => {
  const selectedProject = useAppSelector(
    (state) => state.project.selectedProject,
  );
  const projectId = selectedProject?.id as number | undefined;

  const [dueDays, setDueDays] = useState<number>(7);
  const [coMetric, setCoMetric] = useState<'amount' | 'count'>('amount');

  const { data, isFetching, isLoading, refetch } =
    ProjectManagementDashboardApi.Api.useGetProjectManagementDashboardQuery(
      { query: { project: projectId as number, due_days: dueDays } } as any,
      { skip: !projectId },
    );

  const payload = data?.data;
  const kpis = payload?.kpis;

  const overdue = useMemo(
    () =>
      (payload?.overdue ?? []).filter(
        (item: any) => !isClosedLikeStatus(item?.status),
      ),
    [payload?.overdue],
  );

  const dueSoon = useMemo(
    () =>
      (payload?.due_soon ?? []).filter(
        (item: any) => !isClosedLikeStatus(item?.status),
      ),
    [payload?.due_soon],
  );

  const coByStatus = payload?.change_orders_by_status ?? [];
  const bic = payload?.ball_in_court;

  const rfiBicTop = useMemo(() => (bic?.rfi ?? []).slice(0, 3), [bic?.rfi]);
  const subBicTop = useMemo(
    () => (bic?.submittals ?? []).slice(0, 3),
    [bic?.submittals],
  );
  const coBicTop = useMemo(
    () => (bic?.change_orders ?? []).slice(0, 3),
    [bic?.change_orders],
  );

  const rfiChartData = useMemo(() => {
    if (!kpis) return [];
    return [
      { name: 'Submitted', value: Number(kpis.rfi.open || 0) },
      { name: 'Closed', value: Number(kpis.rfi.closed || 0) },
      {
        name: 'Resubmittal Required',
        value: Number(kpis.rfi.resubmittal_required || 0),
      },
      { name: 'Void', value: Number(kpis.rfi.void || 0) },
      { name: 'Overdue', value: Number(kpis.rfi.overdue || 0) },
    ];
  }, [kpis]);

  const subChartData = useMemo(() => {
    if (!kpis) return [];
    return [
      { name: 'Draft', value: Number(kpis.submittals.draft || 0) },
      {
        name: 'Submitted',
        value: Number(kpis.submittals.submitted_waiting || 0),
      },
      { name: 'Closed', value: Number(kpis.submittals.closed || 0) },
      {
        name: 'Resubmittal Required',
        value: Number(kpis.submittals.returned_action || 0),
      },
      { name: 'Void', value: Number(kpis.submittals.void || 0) },
      { name: 'Overdue', value: Number(kpis.submittals.overdue || 0) },
    ];
  }, [kpis]);

  const coTotals = useMemo(() => {
    const totalCount = (coByStatus ?? []).reduce(
      (acc: number, r: any) => acc + Number(r.count || 0),
      0,
    );
    const totalAmount = (coByStatus ?? []).reduce(
      (acc: number, r: any) => acc + moneyToNumber(r.amount),
      0,
    );
    return { totalCount, totalAmount };
  }, [coByStatus]);

  const contractValue = kpis?.change_orders?.contract_value;
  const currentContractValue = kpis?.change_orders?.current_contract_value;

  if (!projectId)
    return <Center>Select a project to view Project Management.</Center>;
  if (isLoading) return <Center>Loading…</Center>;

  return (
    <Flex flexDirection="column" gapY={4} minH="0" height="full">
      <Card>
        <Flex
          justifyContent="space-between"
          alignItems="start"
          flexWrap="wrap"
          gap={3}
        >
          <VStack align="start" gap={0} flex={1} minW="260px">
            <Text fontWeight="bold" fontSize="lg">
              Project Management Summary
            </Text>
            <Text fontSize="sm" opacity={0.7}>
              {String(
                (selectedProject as any)?.project_code ??
                  (selectedProject as any)?.job_number ??
                  '',
              ) || 'Project'}
              {selectedProject?.item?.name
                ? ` • ${selectedProject?.item?.name}`
                : ''}
              {(selectedProject as any)?.contractor
                ? ` • ${(selectedProject as any)?.contractor}`
                : ''}
            </Text>
          </VStack>

          <Flex
            gap={2}
            flexWrap="wrap"
            justifyContent="flex-end"
            alignItems="center"
          >
            <Text fontSize="sm" opacity={0.7}>
              {isFetching ? 'Refreshing…' : 'Live'}
            </Text>

            <Flex alignItems="center" gap={2}>
              <Text fontSize="sm" opacity={0.7}>
                Due Window
              </Text>
              <NativeSelect.Root size="sm">
                <NativeSelect.Field
                  value={String(dueDays)}
                  onChange={(e) => setDueDays(Number(e.target.value))}
                >
                  <option value="7">Next 7 Days</option>
                  <option value="14">Next 14 Days</option>
                  <option value="30">Next 30 Days</option>
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </Flex>

            <Button
              size="sm"
              variant="outline"
              onClick={async () => {
                try {
                  await (refetch() as any).unwrap?.();
                } catch {
                  toasterApi({
                    type: 'error',
                    status: 500,
                    code: 'Error',
                    message: 'Failed to refresh',
                  });
                }
              }}
              disabled={isFetching}
            >
              Refresh
            </Button>
          </Flex>
        </Flex>
      </Card>

      <Flex gap={3} flexWrap="wrap" align="stretch">
        <DashboardStatusPanel
          title="RFIs"
          subtitle="Snapshot"
          metricLabel="all RFIs"
          chartTitle="Status Distribution"
          chartDescription="Open workload and current status counts"
          kpis={[
            { label: 'Submitted', value: kpis?.rfi?.open, tone: 'gray' },
            {
              label: 'Closed',
              value: kpis?.rfi?.closed,
              tone: kpis?.rfi?.closed ? 'green' : 'gray',
            },
            {
              label: 'Resubmittal Required',
              value: kpis?.rfi?.resubmittal_required,
              tone: kpis?.rfi?.resubmittal_required ? 'red' : 'gray',
            },
            {
              label: 'Void',
              value: kpis?.rfi?.void,
              tone: kpis?.rfi?.void ? 'purple' : 'gray',
            },
            {
              label: 'Overdue',
              value: kpis?.rfi?.overdue,
              tone: kpis?.rfi?.overdue ? 'red' : 'gray',
            },
            {
              label: 'Avg Days Open',
              value: kpis?.rfi?.avg_days_open ?? 0,
              tone: 'gray',
            },
          ]}
          chartData={rfiChartData}
          bicTop={rfiBicTop}
          bicColorPalette="blue"
          isLoading={!kpis}
        />

        <DashboardStatusPanel
          title="Submittals"
          subtitle="Snapshot"
          metricLabel="all Submittals"
          chartTitle="Status Distribution"
          chartDescription="Current submittal mix across the project"
          kpis={[
            {
              label: 'Draft',
              value: kpis?.submittals?.draft,
              tone: 'gray',
            },
            {
              label: 'Submitted',
              value: kpis?.submittals?.submitted_waiting,
              tone: 'gray',
            },
            {
              label: 'Closed',
              value: kpis?.submittals?.closed,
              tone: kpis?.submittals?.closed ? 'green' : 'gray',
            },
            {
              label: 'Resubmittal Required',
              value: kpis?.submittals?.returned_action,
              tone: kpis?.submittals?.returned_action ? 'red' : 'gray',
            },
            {
              label: 'Void',
              value: kpis?.submittals?.void,
              tone: kpis?.submittals?.void ? 'purple' : 'gray',
            },
            {
              label: 'Overdue',
              value: kpis?.submittals?.overdue,
              tone: kpis?.submittals?.overdue ? 'red' : 'gray',
            },
          ]}
          chartData={subChartData}
          bicTop={subBicTop}
          bicColorPalette="teal"
          isLoading={!kpis}
        />

        <Card style={{ flex: '1 1 460px', minHeight: 670 }}>
          <Flex
            justifyContent="space-between"
            alignItems="center"
            mb={2}
            flexWrap="wrap"
            gap={2}
          >
            <Text fontWeight="bold">Change Orders</Text>

            <Flex align="center" gap={2}>
              <Text fontSize="xs" opacity={0.7}>
                Chart
              </Text>
              <NativeSelect.Root size="sm">
                <NativeSelect.Field
                  value={coMetric}
                  onChange={(e) => setCoMetric(e.target.value as any)}
                >
                  <option value="amount">$ Amount</option>
                  <option value="count">Count</option>
                </NativeSelect.Field>
                <NativeSelect.Indicator />
              </NativeSelect.Root>
            </Flex>
          </Flex>

          {!kpis ? (
            <Center py={6}>
              <Spinner />
            </Center>
          ) : (
            <Flex flexDirection="column" gap={3} height="100%">
              <Flex gap={2} flexWrap="wrap">
                <Kpi
                  label="In Review"
                  value={kpis.change_orders.in_review_count}
                  tone={kpis.change_orders.in_review_count ? 'yellow' : 'gray'}
                />
                <Kpi
                  label="Approved"
                  value={kpis.change_orders.approved_count}
                  tone={kpis.change_orders.approved_count ? 'green' : 'gray'}
                />
              </Flex>

              <Box borderWidth="1px" rounded="lg" p={4}>
                <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.08em" opacity={0.65} mb={1}>
                  Contract Summary
                </Text>
                <Text fontWeight="bold" fontSize="2xl">
                  {fmtMoney(currentContractValue)}
                </Text>

                {contractValue ? (
                  <Text fontSize="sm" opacity={0.75}>
                    Contract Value: <b>{fmtMoney(contractValue)}</b>
                  </Text>
                ) : null}
              </Box>

              <Separator />

              <Flex gap={3} flexWrap="wrap" alignItems="center">
                <Text fontSize="sm" opacity={0.8}>
                  In Review:{' '}
                  <b>{fmtMoney(kpis.change_orders.total_in_review)}</b>
                </Text>
                <Text fontSize="sm" opacity={0.8}>
                  Approved: <b>{fmtMoney(kpis.change_orders.total_approved)}</b>
                </Text>
              </Flex>

              <Box borderWidth="1px" rounded="lg" p={3}>
                <ChangeOrderDonut rows={coByStatus} metric={coMetric} />
                <Flex justifyContent="space-between" alignItems="center" mt={1}>
                  <Text fontSize="xs" opacity={0.75}>
                    Total {coMetric === 'amount' ? 'Amount' : 'Count'}:{' '}
                    <b>
                      {coMetric === 'amount'
                        ? fmtMoney(coTotals.totalAmount)
                        : coTotals.totalCount}
                    </b>
                  </Text>
                  <Text fontSize="xs" opacity={0.65}>
                    Breakdown by status
                  </Text>
                </Flex>
              </Box>

              <Flex gap={2} flexWrap="wrap">
                {(coByStatus ?? [])
                  .filter(
                    (r: any) =>
                      String(r.status || '').toLowerCase() !== 'rejected',
                  )
                  .map((r: any) => {
                    const b = statusBadge(r.status);
                    return (
                      <Badge
                        key={r.status}
                        colorPalette={b.colorPalette as any}
                      >
                        <Text textTransform="capitalize">
                          {formatStatusLabel(r.status)}: {r.count} (
                          {fmtMoney(r.amount)})
                        </Text>
                      </Badge>
                    );
                  })}
              </Flex>

              <Box borderWidth="1px" rounded="lg" p={4}>
                <Flex justifyContent="space-between" alignItems="center" mb={3}>
                  <Box>
                    <Text fontWeight="semibold">Ball in Court</Text>
                    <Text fontSize="xs" opacity={0.65}>
                      Top owners from all COs
                    </Text>
                  </Box>
                </Flex>

                <Flex gap={2} flexWrap="wrap">
                  {coBicTop.length ? (
                    coBicTop.map((x: any) => (
                      <Badge key={x.name} colorPalette="purple" variant="subtle" px={2} py={1}>
                        <Text>
                          {x.name}: {x.count}
                        </Text>
                      </Badge>
                    ))
                  ) : (
                    <Text fontSize="sm" opacity={0.7}>
                      —
                    </Text>
                  )}
                </Flex>
              </Box>
            </Flex>
          )}
        </Card>
      </Flex>

      <Flex gap={3} flexWrap="wrap">
        <Card style={{ flex: 1, minWidth: 420, minHeight: 280 }}>
          <Flex justifyContent="space-between" alignItems="center" mb={2}>
            <Text fontWeight="bold">Overdue</Text>
            <Badge colorPalette="red">
              <Text>{overdue.length}</Text>
            </Badge>
          </Flex>

          <Box borderWidth="1px" rounded="md" overflow="hidden">
            <Table.ScrollArea height="260px" maxH="260px">
              <Table.Root size="sm" variant="outline">
                <Table.Header>
                  <Table.Row bg="bg.subtle">
                    <Table.ColumnHeader width="140px">Type</Table.ColumnHeader>
                    <Table.ColumnHeader width="120px">No.</Table.ColumnHeader>
                    <Table.ColumnHeader minW="320px">Title</Table.ColumnHeader>
                    <Table.ColumnHeader width="120px">Due</Table.ColumnHeader>
                    <Table.ColumnHeader width="160px">BIC</Table.ColumnHeader>
                    <Table.ColumnHeader width="160px">
                      Status
                    </Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>

                <Table.Body>
                  {!payload ? (
                    <Table.Row>
                      <Table.Cell colSpan={6}>
                        <Center py={6}>
                          <Spinner />
                        </Center>
                      </Table.Cell>
                    </Table.Row>
                  ) : overdue.length ? (
                    overdue.slice(0, 50).map((r: any) => {
                      const t = typeBadge(r.type);
                      const b = statusBadge(r.status);
                      const bg = rowBgForAction(r);

                      return (
                        <Table.Row key={`${r.type}-${r.id}`} bg={bg as any}>
                          <Table.Cell>
                            <Badge colorPalette={t.colorPalette as any}>
                              <Text>{t.label}</Text>
                            </Badge>
                          </Table.Cell>
                          <Table.Cell fontWeight="bold">{r.number}</Table.Cell>
                          <Table.Cell minW="320px">
                            <Text noOfLines={1}>{r.title}</Text>
                          </Table.Cell>
                          <Table.Cell>{fmtDateTime(r.due_at)}</Table.Cell>
                          <Table.Cell>
                            <Text noOfLines={1}>{r.ball_in_court || '—'}</Text>
                          </Table.Cell>
                          <Table.Cell>
                            <Badge colorPalette={b.colorPalette as any}>
                              <Text>{b.label}</Text>
                            </Badge>
                          </Table.Cell>
                        </Table.Row>
                      );
                    })
                  ) : (
                    <Table.Row>
                      <Table.Cell colSpan={6}>
                        <Center py={6}>
                          <Text opacity={0.7}>No overdue items.</Text>
                        </Center>
                      </Table.Cell>
                    </Table.Row>
                  )}
                </Table.Body>
              </Table.Root>
            </Table.ScrollArea>
          </Box>
        </Card>

        <Card style={{ flex: 1, minWidth: 420, minHeight: 280 }}>
          <Flex justifyContent="space-between" alignItems="center" mb={2}>
            <Text fontWeight="bold">Due Soon (Next {dueDays} Days)</Text>
            <Badge colorPalette="yellow">
              <Text>{dueSoon.length}</Text>
            </Badge>
          </Flex>

          <Box borderWidth="1px" rounded="md" overflow="hidden">
            <Table.ScrollArea height="260px" maxH="260px">
              <Table.Root size="sm" variant="outline">
                <Table.Header>
                  <Table.Row bg="bg.subtle">
                    <Table.ColumnHeader width="140px">Type</Table.ColumnHeader>
                    <Table.ColumnHeader width="120px">No.</Table.ColumnHeader>
                    <Table.ColumnHeader minW="320px">Title</Table.ColumnHeader>
                    <Table.ColumnHeader width="120px">Due</Table.ColumnHeader>
                    <Table.ColumnHeader width="160px">BIC</Table.ColumnHeader>
                    <Table.ColumnHeader width="160px">
                      Status
                    </Table.ColumnHeader>
                  </Table.Row>
                </Table.Header>

                <Table.Body>
                  {!payload ? (
                    <Table.Row>
                      <Table.Cell colSpan={6}>
                        <Center py={6}>
                          <Spinner />
                        </Center>
                      </Table.Cell>
                    </Table.Row>
                  ) : dueSoon.length ? (
                    dueSoon.slice(0, 50).map((r: any) => {
                      const t = typeBadge(r.type);
                      const b = statusBadge(r.status);
                      const bg = rowBgForAction(r);

                      return (
                        <Table.Row key={`${r.type}-${r.id}`} bg={bg as any}>
                          <Table.Cell>
                            <Badge colorPalette={t.colorPalette as any}>
                              <Text>{t.label}</Text>
                            </Badge>
                          </Table.Cell>
                          <Table.Cell fontWeight="bold">{r.number}</Table.Cell>
                          <Table.Cell minW="320px">
                            <Text noOfLines={1}>{r.title}</Text>
                          </Table.Cell>
                          <Table.Cell>{fmtDateTime(r.due_at)}</Table.Cell>
                          <Table.Cell>
                            <Text noOfLines={1}>{r.ball_in_court || '—'}</Text>
                          </Table.Cell>
                          <Table.Cell>
                            <Badge colorPalette={b.colorPalette as any}>
                              <Text>{b.label}</Text>
                            </Badge>
                          </Table.Cell>
                        </Table.Row>
                      );
                    })
                  ) : (
                    <Table.Row>
                      <Table.Cell colSpan={6}>
                        <Center py={6}>
                          <Text opacity={0.7}>No upcoming due items.</Text>
                        </Center>
                      </Table.Cell>
                    </Table.Row>
                  )}
                </Table.Body>
              </Table.Root>
            </Table.ScrollArea>
          </Box>
        </Card>
      </Flex>
    </Flex>
  );
};

export default Plan;

// -----------------------------
// components
// -----------------------------
const SectionHeader = ({
  title,
  description,
}: {
  title: string;
  description?: string;
}) => {
  return (
    <Box mb={3}>
      <Text fontWeight="semibold">{title}</Text>
      {description ? (
        <Text fontSize="xs" opacity={0.65}>
          {description}
        </Text>
      ) : null}
    </Box>
  );
};

const DashboardStatusPanel = ({
  title,
  subtitle,
  metricLabel,
  chartTitle,
  chartDescription,
  kpis,
  chartData,
  bicTop,
  bicColorPalette,
  isLoading,
}: {
  title: string;
  subtitle?: string;
  metricLabel: string;
  chartTitle: string;
  chartDescription?: string;
  kpis: { label: string; value: any; tone?: 'red' | 'yellow' | 'green' | 'gray' | 'purple' }[];
  chartData: { name: string; value: number }[];
  bicTop: any[];
  bicColorPalette: string;
  isLoading?: boolean;
}) => {
  return (
    <Card style={{ flex: '1 1 420px', minHeight: 670 }}>
      <Flex justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Text fontWeight="bold">{title}</Text>
          <Text fontSize="xs" opacity={0.65}>
            {subtitle || 'Snapshot'}
          </Text>
        </Box>
      </Flex>

      {isLoading ? (
        <Center py={6}>
          <Spinner />
        </Center>
      ) : (
        <Flex flexDirection="column" gap={4} height="100%">
          <Box borderWidth="1px" rounded="lg" p={4}>
            <SectionHeader
              title="Snapshot"
              description="Key status totals at a glance"
            />
            <Box
              display="grid"
              gridTemplateColumns="repeat(2, minmax(0, 1fr))"
              gap={3}
            >
              {kpis.map((item) => (
                <Kpi
                  key={item.label}
                  label={item.label}
                  value={item.value}
                  tone={item.tone}
                />
              ))}
            </Box>
          </Box>

          <Box borderWidth="1px" rounded="lg" p={4}>
            <SectionHeader
              title={chartTitle}
              description={chartDescription}
            />
            <CompactBarChart data={chartData} height={250} valueLabel="Count" />
          </Box>

          <Box borderWidth="1px" rounded="lg" p={4}>
            <Flex justifyContent="space-between" alignItems="center" mb={3}>
              <Box>
                <Text fontWeight="semibold">Ball in Court</Text>
                <Text fontSize="xs" opacity={0.65}>
                  Top owners from {metricLabel}
                </Text>
              </Box>
            </Flex>

            <Flex gap={2} flexWrap="wrap">
              {bicTop.length ? (
                bicTop.map((x: any) => (
                  <Badge
                    key={x.name}
                    colorPalette={bicColorPalette as any}
                    variant="subtle"
                    px={2}
                    py={1}
                  >
                    <Text>
                      {x.name}: {x.count}
                    </Text>
                  </Badge>
                ))
              ) : (
                <Text fontSize="sm" opacity={0.7}>
                  —
                </Text>
              )}
            </Flex>
          </Box>
        </Flex>
      )}
    </Card>
  );
};

const kpiToneMeta = (
  tone?: 'red' | 'yellow' | 'green' | 'gray' | 'purple',
) => {
  if (tone === 'green') {
    return {
      label: 'Closed',
      borderColor: { base: 'green.200', _dark: 'green.800' },
      accent: { base: 'green.500', _dark: 'green.400' },
    };
  }

  if (tone === 'red') {
    return {
      label: 'Action',
      borderColor: { base: 'red.200', _dark: 'red.800' },
      accent: { base: 'red.500', _dark: 'red.400' },
    };
  }

  if (tone === 'purple') {
    return {
      label: 'Void',
      borderColor: { base: 'purple.200', _dark: 'purple.800' },
      accent: { base: 'purple.500', _dark: 'purple.400' },
    };
  }

  if (tone === 'yellow') {
    return {
      label: 'Review',
      borderColor: { base: 'yellow.200', _dark: 'yellow.700' },
      accent: { base: 'yellow.500', _dark: 'yellow.300' },
    };
  }

  return {
    label: 'Open',
    borderColor: { base: 'gray.200', _dark: 'gray.700' },
    accent: { base: 'gray.400', _dark: 'gray.500' },
  };
};

const Kpi = ({
  label,
  value,
  tone,
}: {
  label: string;
  value: any;
  tone?: 'red' | 'yellow' | 'green' | 'gray' | 'purple';
}) => {
  const meta = kpiToneMeta(tone);

  return (
    <Flex
      position="relative"
      flexDirection="column"
      borderWidth="1px"
      borderColor={meta.borderColor as any}
      rounded="lg"
      px={3}
      py={3}
      minH="88px"
      bg={{ base: 'white', _dark: 'rgba(255,255,255,0.02)' }}
      overflow="hidden"
    >
      <Box
        position="absolute"
        left="0"
        top="0"
        bottom="0"
        width="4px"
        bg={meta.accent as any}
      />
      <Flex justify="space-between" align="start" gap={2} pl={1}>
        <Text fontSize="xs" opacity={0.72} lineHeight="1.3">
          {label}
        </Text>
        <Badge colorPalette={toneBadge(tone) as any} variant="subtle">
          <Text>{meta.label}</Text>
        </Badge>
      </Flex>

      <Text fontWeight="bold" fontSize="2xl" mt={3} pl={1} lineHeight="1">
        {String(value ?? 0)}
      </Text>
    </Flex>
  );
};